/*
 * @Copyright 2023-2025. Institute for Future Intelligence, Inc.
 */

// Selectors over PrimitiveStoreState (used with `usePrimitiveStore(...)`).
// Holds ephemeral UI / runtime flags — not persisted, not part of projectState.

import { PrimitiveStoreState } from '../commonPrimitive';

export const setPrimitiveStore = (state: PrimitiveStoreState) => state.setPrimitiveStore;

// landing page
export const showLanding = (state: PrimitiveStoreState) => state.showLanding;

// lesson plan creator modal (generated from the open project)
export const showLessonPlanModal = (state: PrimitiveStoreState) => state.showLessonPlanModal;
export const lessonPlanToOpen = (state: PrimitiveStoreState) => state.lessonPlanToOpen;
export const showLessonPlanListPanel = (state: PrimitiveStoreState) => state.showLessonPlanListPanel;

// version / async
export const latestVersion = (state: PrimitiveStoreState) => state.latestVersion;
export const waiting = (state: PrimitiveStoreState) => state.waiting;
export const setWaiting = (state: PrimitiveStoreState) => state.setWaiting;
export const deepLinkPending = (state: PrimitiveStoreState) => state.deepLinkPending;
export const generating = (state: PrimitiveStoreState) => state.generating;
export const setGenerating = (state: PrimitiveStoreState) => state.setGenerating;
export const predicting = (state: PrimitiveStoreState) => state.predicting;
export const setPredicting = (state: PrimitiveStoreState) => state.setPredicting;
export const reasoningStreamTitle = (state: PrimitiveStoreState) => state.reasoningStreamTitle;
export const setReasoningStreamTitle = (state: PrimitiveStoreState) => state.setReasoningStreamTitle;
export const reasoningPrompt = (state: PrimitiveStoreState) => state.reasoningPrompt;
export const setReasoningPrompt = (state: PrimitiveStoreState) => state.setReasoningPrompt;
export const agentRunning = (state: PrimitiveStoreState) => state.agentRunning;
export const agentChatAttachments = (state: PrimitiveStoreState) => state.agentChatAttachments;
export const setAgentRunning = (state: PrimitiveStoreState) => state.setAgentRunning;
export const reasoningStream = (state: PrimitiveStoreState) => state.reasoningStream;
export const appendReasoningStream = (state: PrimitiveStoreState) => state.appendReasoningStream;
export const clearReasoningStream = (state: PrimitiveStoreState) => state.clearReasoningStream;
export const showReasoning = (state: PrimitiveStoreState) => state.showReasoning;
export const setShowReasoning = (state: PrimitiveStoreState) => state.setShowReasoning;
export const reasoningStartTime = (state: PrimitiveStoreState) => state.reasoningStartTime;
export const reasoningElapsedMs = (state: PrimitiveStoreState) => state.reasoningElapsedMs;
export const startReasoningTimer = (state: PrimitiveStoreState) => state.startReasoningTimer;
export const stopReasoningTimer = (state: PrimitiveStoreState) => state.stopReasoningTimer;
export const ephemeralChatMessages = (state: PrimitiveStoreState) => state.ephemeralChatMessages;
export const addEphemeralChatMessage = (state: PrimitiveStoreState) => state.addEphemeralChatMessage;
export const removeEphemeralChatMessage = (state: PrimitiveStoreState) => state.removeEphemeralChatMessage;
export const clearEphemeralChatMessages = (state: PrimitiveStoreState) => state.clearEphemeralChatMessages;
export const changed = (state: PrimitiveStoreState) => state.changed;
export const setChanged = (state: PrimitiveStoreState) => state.setChanged;
export const minimizeLabFlag = (state: PrimitiveStoreState) => state.minimizeLabFlag;
export const minimizing = (state: PrimitiveStoreState) => state.minimizing;
export const minimizingEnergy = (state: PrimitiveStoreState) => state.minimizingEnergy;

// update flags (toggled to trigger re-renders / effects)
export const updateViewerFlag = (state: PrimitiveStoreState) => state.updateViewerFlag;
export const updateViewer = (state: PrimitiveStoreState) => state.updateViewer;
export const updateInfoFlag = (state: PrimitiveStoreState) => state.updateInfoFlag;
export const updateInfo = (state: PrimitiveStoreState) => state.updateInfo;
export const updateDataFlag = (state: PrimitiveStoreState) => state.updateDataFlag;

// picking / selection
export const selectedPlane = (state: PrimitiveStoreState) => state.selectedPlane;
export const pickMode = (state: PrimitiveStoreState) => state.pickMode;
export const pickedAtomIndex = (state: PrimitiveStoreState) => state.pickedAtomIndex;
export const pickedAtomIndices = (state: PrimitiveStoreState) => state.pickedAtomIndices;
export const pickedMoleculeIndex = (state: PrimitiveStoreState) => state.pickedMoleculeIndex;
export const pickedMoleculeIndices = (state: PrimitiveStoreState) => state.pickedMoleculeIndices;
export const copiedMolecule = (state: PrimitiveStoreState) => state.copiedMolecule;
export const cutMolecule = (state: PrimitiveStoreState) => state.cutMolecule;

// docking
export const ligandScoreAtoms = (state: PrimitiveStoreState) => state.ligandScoreAtoms;
export const ligandTorsionInput = (state: PrimitiveStoreState) => state.ligandTorsionInput;
export const ligandConformationRmsd = (state: PrimitiveStoreState) => state.ligandConformationRmsd;
export const ligandRotatableBondCount = (state: PrimitiveStoreState) => state.ligandRotatableBondCount;
export const detectedPockets = (state: PrimitiveStoreState) => state.detectedPockets;
export const proteinResidueLabels = (state: PrimitiveStoreState) => state.proteinResidueLabels;
export const ligandPharmacophoreFeatures = (state: PrimitiveStoreState) => state.ligandPharmacophoreFeatures;
export const receptorPharmacophoreFeatures = (state: PrimitiveStoreState) => state.receptorPharmacophoreFeatures;
export const relaxingLigand = (state: PrimitiveStoreState) => state.relaxingLigand;
export const relaxApproaching = (state: PrimitiveStoreState) => state.relaxApproaching;
export const dockingScore = (state: PrimitiveStoreState) => state.dockingScore;
export const searchingLigand = (state: PrimitiveStoreState) => state.searchingLigand;
export const searchProgress = (state: PrimitiveStoreState) => state.searchProgress;
export const searchResults = (state: PrimitiveStoreState) => state.searchResults;
export const dockingPoseHistory = (state: PrimitiveStoreState) => state.dockingPoseHistory;
export const liveLigandTransform = (state: PrimitiveStoreState) => state.liveLigandTransform;
export const screeningActive = (state: PrimitiveStoreState) => state.screeningActive;
export const screeningProgress = (state: PrimitiveStoreState) => state.screeningProgress;
export const pocketGenerating = (state: PrimitiveStoreState) => state.pocketGenerating;
export const pocketGenerationProgress = (state: PrimitiveStoreState) => state.pocketGenerationProgress;

// account / users
export const userCount = (state: PrimitiveStoreState) => state.userCount;
export const pendingAcceptance = (state: PrimitiveStoreState) => state.pendingAcceptance;

// classroom
export const showSubmitToClassModal = (state: PrimitiveStoreState) => state.showSubmitToClassModal;

// export
export const showExportModelModal = (state: PrimitiveStoreState) => state.showExportModelModal;
export const exportRenderedRequest = (state: PrimitiveStoreState) => state.exportRenderedRequest;
export const exportProgress = (state: PrimitiveStoreState) => state.exportProgress;

// hover / drag
export const hoveredMolecule = (state: PrimitiveStoreState) => state.hoveredMolecule;
export const hoveredCrystalName = (state: PrimitiveStoreState) => state.hoveredCrystalName;
export const hoveredBiomoleculeName = (state: PrimitiveStoreState) => state.hoveredBiomoleculeName;
export const bioModeAnimation = (state: PrimitiveStoreState) => state.bioModeAnimation;
export const bioMobilityColoring = (state: PrimitiveStoreState) => state.bioMobilityColoring;
export const bioConservation = (state: PrimitiveStoreState) => state.bioConservation;
export const bioThumbnails = (state: PrimitiveStoreState) => state.bioThumbnails;
export const bioInspectMode = (state: PrimitiveStoreState) => state.bioInspectMode;
export const bioPicks = (state: PrimitiveStoreState) => state.bioPicks;
export const bioInteractionOverlay = (state: PrimitiveStoreState) => state.bioInteractionOverlay;
export const bioHighlight = (state: PrimitiveStoreState) => state.bioHighlight;
export const bioHoverResidue = (state: PrimitiveStoreState) => state.bioHoverResidue;
export const bioFocusRequest = (state: PrimitiveStoreState) => state.bioFocusRequest;
export const bioExplode = (state: PrimitiveStoreState) => state.bioExplode;
export const bioComparison = (state: PrimitiveStoreState) => state.bioComparison;
export const bioMorph = (state: PrimitiveStoreState) => state.bioMorph;
export const bioPartialMD = (state: PrimitiveStoreState) => state.bioPartialMD;
export const bioPartialMDStatus = (state: PrimitiveStoreState) => state.bioPartialMDStatus;
export const bioPartialMDSeries = (state: PrimitiveStoreState) => state.bioPartialMDSeries;
export const bioSimAnalysisVisible = (state: PrimitiveStoreState) => state.bioSimAnalysisVisible;
export const bioPartialMDResetSignal = (state: PrimitiveStoreState) => state.bioPartialMDResetSignal;
export const bioPartialMDMinimizing = (state: PrimitiveStoreState) => state.bioPartialMDMinimizing;
export const bioPartialMDVelScale = (state: PrimitiveStoreState) => state.bioPartialMDVelScale;
export const bioLigandScore = (state: PrimitiveStoreState) => state.bioLigandScore;
export const bioLigandMdAtoms = (state: PrimitiveStoreState) => state.bioLigandMdAtoms;
export const bioLigandReport = (state: PrimitiveStoreState) => state.bioLigandReport;
export const bioLigandDocking = (state: PrimitiveStoreState) => state.bioLigandDocking;
export const bioRegionSelect = (state: PrimitiveStoreState) => state.bioRegionSelect;
export const bioRegionRadius = (state: PrimitiveStoreState) => state.bioRegionRadius;
export const pickedCrystalMoleculeIndex = (state: PrimitiveStoreState) => state.pickedCrystalMoleculeIndex;
export const pickedCrystalAtomIndex = (state: PrimitiveStoreState) => state.pickedCrystalAtomIndex;
export const pickedCrystalAtomIsGhost = (state: PrimitiveStoreState) => state.pickedCrystalAtomIsGhost;
export const crystalGhostAtomCount = (state: PrimitiveStoreState) => state.crystalGhostAtomCount;
export const hoveredMoleculeGroup = (state: PrimitiveStoreState) => state.hoveredMoleculeGroup;
export const bitHighlightedMoleculeNames = (state: PrimitiveStoreState) => state.bitHighlightedMoleculeNames;
export const dragAndDropMolecule = (state: PrimitiveStoreState) => state.dragAndDropMolecule;

// camera / rotation control
export const enableRotate = (state: PrimitiveStoreState) => state.enableRotate;
export const autoRotate = (state: PrimitiveStoreState) => state.autoRotate;
export const vrMode = (state: PrimitiveStoreState) => state.vrMode;

// simulation flags
export const mdPauseSettling = (state: PrimitiveStoreState) => state.mdPauseSettling;
export const startSimulation = (state: PrimitiveStoreState) => state.startSimulation;
export const resetSimulation = (state: PrimitiveStoreState) => state.resetSimulation;

// view flags
export const resetViewFlag = (state: PrimitiveStoreState) => state.resetViewFlag;
export const zoomViewFlag = (state: PrimitiveStoreState) => state.zoomViewFlag;

// project lifecycle flags (one-shot signals consumed by useFlag)
export const createProjectFlag = (state: PrimitiveStoreState) => state.createProjectFlag;
export const createProjectDialog = (state: PrimitiveStoreState) => state.createProjectDialog;
export const saveProjectFlag = (state: PrimitiveStoreState) => state.saveProjectFlag;
export const saveAndThenOpenProjectFlag = (state: PrimitiveStoreState) => state.saveAndThenOpenProjectFlag;
export const openRecentProjectFlag = (state: PrimitiveStoreState) => state.openRecentProjectFlag;
export const saveProjectAsFlag = (state: PrimitiveStoreState) => state.saveProjectAsFlag;
export const saveProjectAsDialog = (state: PrimitiveStoreState) => state.saveProjectAsDialog;
export const showSupercellSizeModal = (state: PrimitiveStoreState) => state.showSupercellSizeModal;
export const setShowSupercellSizeModal = (state: PrimitiveStoreState) => state.setShowSupercellSizeModal;
export const showGenerateCrystalModal = (state: PrimitiveStoreState) => state.showGenerateCrystalModal;
export const setShowGenerateCrystalModal = (state: PrimitiveStoreState) => state.setShowGenerateCrystalModal;
export const showBuildChainModal = (state: PrimitiveStoreState) => state.showBuildChainModal;
export const coarseGrainMoleculeName = (state: PrimitiveStoreState) => state.coarseGrainMoleculeName;
export const setCoarseGrainMoleculeName = (state: PrimitiveStoreState) => state.setCoarseGrainMoleculeName;
export const setShowBuildChainModal = (state: PrimitiveStoreState) => state.setShowBuildChainModal;
export const showMonomerInputModal = (state: PrimitiveStoreState) => state.showMonomerInputModal;
export const setShowMonomerInputModal = (state: PrimitiveStoreState) => state.setShowMonomerInputModal;
export const pendingCrystalChatSubmit = (state: PrimitiveStoreState) => state.pendingCrystalChatSubmit;
export const setPendingCrystalChatSubmit = (state: PrimitiveStoreState) => state.setPendingCrystalChatSubmit;
export const showProjectsFlag = (state: PrimitiveStoreState) => state.showProjectsFlag;
export const updateProjectsFlag = (state: PrimitiveStoreState) => state.updateProjectsFlag;
export const showProjectListPanel = (state: PrimitiveStoreState) => state.showProjectListPanel;
export const showExamplesDrawer = (state: PrimitiveStoreState) => state.showExamplesDrawer;
export const askForScreenName = (state: PrimitiveStoreState) => state.askForScreenName;
export const recordDipoleCurrent = (state: PrimitiveStoreState) => state.recordDipoleCurrent;
export const computeIrSpectrumFlag = (state: PrimitiveStoreState) => state.computeIrSpectrumFlag;
export const spectrumAnnotations = (state: PrimitiveStoreState) => state.spectrumAnnotations;
export const gOfRAnnotations = (state: PrimitiveStoreState) => state.gOfRAnnotations;
export const energyAnnotations = (state: PrimitiveStoreState) => state.energyAnnotations;
export const speedAnnotations = (state: PrimitiveStoreState) => state.speedAnnotations;
export const msdAnnotations = (state: PrimitiveStoreState) => state.msdAnnotations;
export const dosAnnotations = (state: PrimitiveStoreState) => state.dosAnnotations;
export const computeMsdFlag = (state: PrimitiveStoreState) => state.computeMsdFlag;
export const computeVdosFlag = (state: PrimitiveStoreState) => state.computeVdosFlag;
export const computeGOfRFlag = (state: PrimitiveStoreState) => state.computeGOfRFlag;
export const openBandStructureFlag = (state: PrimitiveStoreState) => state.openBandStructureFlag;
export const engineRebuildFlag = (state: PrimitiveStoreState) => state.engineRebuildFlag;
export const syncEngineFlag = (state: PrimitiveStoreState) => state.syncEngineFlag;
export const sceneReloadFlag = (state: PrimitiveStoreState) => state.sceneReloadFlag;
export const pendingTempChangePercent = (state: PrimitiveStoreState) => state.pendingTempChangePercent;
export const pendingPistonPlate = (state: PrimitiveStoreState) => state.pendingPistonPlate;
export const mdWorkerActive = (state: PrimitiveStoreState) => state.mdWorkerActive;
export const mdControlReplySeq = (state: PrimitiveStoreState) => state.mdControlReplySeq;
export const crystalWorkerActive = (state: PrimitiveStoreState) => state.crystalWorkerActive;
export const crystalFixedRequest = (state: PrimitiveStoreState) => state.crystalFixedRequest;
export const crystalAtomStateRequest = (state: PrimitiveStoreState) => state.crystalAtomStateRequest;
export const crystalFrameSyncRequest = (state: PrimitiveStoreState) => state.crystalFrameSyncRequest;
export const crystalTemperatureRequest = (state: PrimitiveStoreState) => state.crystalTemperatureRequest;
export const pendingChamberMoleculeName = (state: PrimitiveStoreState) => state.pendingChamberMoleculeName;
export const pendingChamberMoleculeCount = (state: PrimitiveStoreState) => state.pendingChamberMoleculeCount;
export const pendingChamberMoleculeRegion = (state: PrimitiveStoreState) => state.pendingChamberMoleculeRegion;
export const pendingChamberMoleculePoint = (state: PrimitiveStoreState) => state.pendingChamberMoleculePoint;
export const dipoleCurrentSampleEveryNSteps = (state: PrimitiveStoreState) => state.dipoleCurrentSampleEveryNSteps;
export const recordVelocity = (state: PrimitiveStoreState) => state.recordVelocity;
export const velocitySampleEveryNSteps = (state: PrimitiveStoreState) => state.velocitySampleEveryNSteps;
export const recordPosition = (state: PrimitiveStoreState) => state.recordPosition;
export const positionSampleEveryNSteps = (state: PrimitiveStoreState) => state.positionSampleEveryNSteps;
export const recordMeasurement = (state: PrimitiveStoreState) => state.recordMeasurement;
export const agentInquiry = (state: PrimitiveStoreState) => state.agentInquiry;
export const agentDesign = (state: PrimitiveStoreState) => state.agentDesign;
export const shareProjectFlag = (state: PrimitiveStoreState) => state.shareProjectFlag;
export const removeFromCommonsFlag = (state: PrimitiveStoreState) => state.removeFromCommonsFlag;

// regression
export const regressionAnalysis = (state: PrimitiveStoreState) => state.regressionAnalysis;
export const regression = (state: PrimitiveStoreState) => state.regression;

// spaceship runtime state
export const showThrustFlame = (state: PrimitiveStoreState) => state.showThrustFlame;
export const navCoordinates = (state: PrimitiveStoreState) => state.navCoordinates;

// current simulation readouts
export const currentTemperature = (state: PrimitiveStoreState) => state.currentTemperature;
export const currentPressure = (state: PrimitiveStoreState) => state.currentPressure;
export const currentDensity = (state: PrimitiveStoreState) => state.currentDensity;
export const reactiveSpeciesBaseline = (state: PrimitiveStoreState) => state.reactiveSpeciesBaseline;
export const crystalMsPerStep = (state: PrimitiveStoreState) => state.crystalMsPerStep;

// misc UI
export const showPeriodicTable = (state: PrimitiveStoreState) => state.showPeriodicTable;
export const message = (state: PrimitiveStoreState) => state.message;
export const moleculeDataUpdated = (state: PrimitiveStoreState) => state.moleculeDataUpdated;

// Gallery-card edit menu state — keyed by molecule name so a single context
// menu can be shared by the whole gallery and the right card "wins" the pick.
export const galleryEditPickedMoleculeName = (state: PrimitiveStoreState) => state.galleryEditPickedMoleculeName;
export const galleryEditPickedAtomIndex = (state: PrimitiveStoreState) => state.galleryEditPickedAtomIndex;
export const galleryEditMenuAnchor = (state: PrimitiveStoreState) => state.galleryEditMenuAnchor;
export const galleryEditBusy = (state: PrimitiveStoreState) => state.galleryEditBusy;
export const galleryEditCustomSmilesFor = (state: PrimitiveStoreState) => state.galleryEditCustomSmilesFor;
export const galleryEditAddBondFor = (state: PrimitiveStoreState) => state.galleryEditAddBondFor;
export const uvVisSpectrumForMoleculeName = (state: PrimitiveStoreState) => state.uvVisSpectrumForMoleculeName;
export const uvVisSpectrumEngine = (state: PrimitiveStoreState) => state.uvVisSpectrumEngine;

// auto-scroll signal for gallery (most recently added molecule name)
export const latestAddedMoleculeName = (state: PrimitiveStoreState) => state.latestAddedMoleculeName;
export const setLatestAddedMoleculeName = (state: PrimitiveStoreState) => state.setLatestAddedMoleculeName;
